import {
  AreaCategory,
  BookingProcess,
  BookingProcessInfo,
  BookingSteps,
  buildDayForBooking,
  getKnownAreaCategories,
  HomeTimeSlot,
  IAdditionalOptions,
  IAvailableStudioTimeSlots,
  IDisplayableUserProfile,
  ITimeSlot,
  ITimeSlotViewModel,
  ITimeSlotWithEligibility,
  IUserRecommendedSettings,
  IWorkoutTypeClassTimeData,
  OnDemandBookingSlotData,
  ProcessTypes,
  selectRecommendedSettingsBasedOnArea,
  UserProfile,
  UserWorkoutSession,
  WaitlistEntry,
  WorkoutSettings,
} from 'fitbeat.gym-core';
import {
  BaseUserProfile,
  BookingProcessProgressBar,
  CardioOrResistance,
  Gym,
  GymTypes,
  IDay,
  IEnabledFeatures,
  ILiveStreamOptions,
  ProgramType,
  WorkoutType,
} from 'fitbeat.models';
import produce from 'immer';
import moment from 'moment-timezone';
import { Platform } from 'react-native';
import * as actionTypes from '../../auth/actionTypes';
import { getConfig } from '../../config';
import { INVITE_SCREEN_UNMOUNTED } from '../../Planner';
import { GymResolver } from '../../services';
import { i18n } from '../../utils';
import * as types from '../actionTypes';
import {
  BOOK_WORKOUT_SLIDER_CARD_PRESSED, SCHEDULE_OR_START_WORKOUT_BUTTON_PRESSED,
  CHANGE_WAITLIST_ENTRY,
  REMOVE_WAITLIST_ENTRY,
  WAITINGLIST_BOOKING_FAILURE,
  WAITINGLIST_BOOKING_REQUEST,
  WAITINGLIST_BOOKING_SUCCESS,
} from '../actionTypes';
import { PersonaliseSetupWarning } from '../steps/personaliseWorkoutSteps/PersonaliseWorkoutSetup';
import { PersonaliseSetupScreens } from '../steps/personaliseWorkoutSteps/types';
import { getWorkoutOrInvite, minutesToAllowBookingsAfterClassStarted } from '../utils/BookingUtils';

const config = getConfig();

const knownAreasWhenAppWasBuilt = getKnownAreaCategories();

export interface IStudioSlots {
  thirtyMinuteWorkoutSlots: ITimeSlotWithEligibility[];
  fiftyMinuteWorkoutSlots: ITimeSlotWithEligibility[];
}

export interface ITimeSlotUIViewModel extends ITimeSlotViewModel {
  isSelected: boolean;
}

export interface IWorkoutTypeClassTimeDataViewModel extends IWorkoutTypeClassTimeData {
  classTimes: ITimeSlotUIViewModel[];
}

export interface IAvailableStudioTimeSlotsViewModel extends IAvailableStudioTimeSlots {
  availableTimeSlots: IWorkoutTypeClassTimeDataViewModel[];
}

export interface IBookingWizardState {
  workoutSession: UserWorkoutSession | null;
  currentBookingStep: BookingSteps | undefined;
  processType: ProcessTypes | undefined;
  invites: UserWorkoutSession[];
  partnerProfiles: { [key: string]: UserProfile };
  waitlistEntry: WaitlistEntry | null;
  isFetchingPartnerProfiles: boolean;
  longTermGoal: ProgramType;
  isCapturingBasicInfo: boolean;
  currentSelectedGymName: string | null;
  isOfficeScheduleBooking: boolean | null;
  isSoloWorkout: boolean | null;
  previousBookingSteps: BookingProcessInfo[];
  isLoadingBookingSlots: boolean;
  homeSlots: HomeTimeSlot[];
  studioSlots: IStudioSlots;
  studioZones: IAvailableStudioTimeSlotsViewModel[];
  bookingSlotsError: string | null;
  pendingSlot: any;
  workoutType: WorkoutType | null;
  areaCategory: AreaCategory | undefined;
  makeBookingLoading: boolean;
  makeBookingError: string | null;
  recommendedSettingsLoading: boolean;
  allUserRecommendedSettings: IUserRecommendedSettings | null;
  /**
   * TODO: rename this
   * it is not the recommended settings, but more the workoutSettings, which is initialized
   * from the recommended settings, but then updated by the user
   */
  recommendedSettings: WorkoutSettings | null;
  skillLevelLoading: boolean;
  skillLevelError: string | null;
  progressBar?: BookingProcessProgressBar;
  currentPersonaliseSetupScreen: PersonaliseSetupScreens | null;
  personaliseSetupWarning: PersonaliseSetupWarning | null;
  studioPartner: IDisplayableUserProfile | null;
  isCustomisingWorkout: boolean;
  day: IDay | null;
  isBookingModalVisible: boolean;
  showSupportedDevices: boolean;
  creatingDemoBookingInProgress: boolean;
  waitlistLoading: boolean;
  bookWaitlistSlotLoading: boolean;
  bookWaitlistSlotError: string | null;
  checkInLoading: boolean;
  checkInError: string | null;
  showStartOrScheduleOnDurationStep: boolean;
  livestreamOptions?: ILiveStreamOptions;
  wokoutTypeWarning?: CardioOrResistance;
  livestreamToggleValue: boolean;
  // todo: some of this on demand slots is probably unused, since ondemand is now part of overall slots
  onDemandSlots?: ITimeSlot[];
  startOnDemandBookingLoading: boolean;
  loadingOnDemandSlots: boolean;
  onDemandStudioBooking: boolean;
  checkingInOnDemandWorkout: boolean;
  cancelOnDemandBookingLoading: boolean;
  updatingBoxingSetting: boolean;
  subscriptionPortalLoading: boolean;
  fromPlanner: boolean;
  isFirstWorkout: boolean;
}

export const defaultWizardState: IBookingWizardState = {
  processType: undefined,
  workoutSession: null,
  currentBookingStep: undefined,
  invites: [],
  partnerProfiles: {},
  waitlistEntry: null,
  isFetchingPartnerProfiles: false,
  longTermGoal: ProgramType.NotSet,
  isCapturingBasicInfo: false,
  currentSelectedGymName: null,
  isSoloWorkout: null,
  previousBookingSteps: [],
  isLoadingBookingSlots: false,
  homeSlots: [],
  studioSlots: {
    thirtyMinuteWorkoutSlots: [],
    fiftyMinuteWorkoutSlots: [],
  },
  studioZones: [],
  bookingSlotsError: null,
  pendingSlot: null,
  makeBookingLoading: false,
  makeBookingError: null,
  workoutType: null,
  areaCategory: undefined,
  recommendedSettingsLoading: false,
  recommendedSettings: null,
  allUserRecommendedSettings: null,
  skillLevelLoading: false,
  skillLevelError: null,
  progressBar: undefined,
  currentPersonaliseSetupScreen: PersonaliseSetupScreens.Overview,
  personaliseSetupWarning: null,
  studioPartner: null,
  isCustomisingWorkout: false,
  day: null,
  isBookingModalVisible: false,
  showSupportedDevices: false,
  creatingDemoBookingInProgress: false,
  waitlistLoading: false,
  bookWaitlistSlotLoading: false,
  bookWaitlistSlotError: null,
  checkInLoading: false,
  checkInError: null,
  showStartOrScheduleOnDurationStep: false,
  livestreamToggleValue: false,
  startOnDemandBookingLoading: false,
  loadingOnDemandSlots: false,
  onDemandStudioBooking: false,
  checkingInOnDemandWorkout: false,
  cancelOnDemandBookingLoading: false,
  updatingBoxingSetting: false,
  subscriptionPortalLoading: false,
  fromPlanner: false,
  isFirstWorkout: false,
};

const getCurrentBookingProcessInfo = (
  state: IBookingWizardState,
  action: any,
  currentBookingStep: BookingSteps,
  processType: ProcessTypes,
  enabledFeatures: IEnabledFeatures,
): BookingProcessInfo => {
  const {
    workoutSession,
    invites,
    waitlistEntry,
    isCapturingBasicInfo,
    currentSelectedGymName,
    onDemandStudioBooking,
    isOfficeScheduleBooking,
  } = state;
  const {
    userProfile,
    allBookings,
    workoutInviteId,
    isSoloWorkout,
  } = action.payload;

  const workout = getWorkoutOrInvite(workoutSession, invites);
  const selectedGym = (currentSelectedGymName && GymResolver.getByName(currentSelectedGymName)) || undefined;

  const options: IAdditionalOptions = {
    isCapturingBasicInfo,
    allBookings,
    workoutInviteId,
    isSoloWorkout,
    selectedGym,
    isInviteFriendsEnabledForStudio: true,
    day: state.day,
    enabledFeatures,
    onDemandStudioBooking,
    platformOS: Platform.OS,
    fromPlanner: state.fromPlanner,
    isOfficeScheduleBooking: isOfficeScheduleBooking ?? false,
    isFirstWorkout: state.isFirstWorkout,
  };
  return BookingProcess.getNextStep(
    userProfile,
    workout,
    processType,
    options,
    waitlistEntry || undefined,
    currentBookingStep,
  );
};

const getIsBasicInfoStep = (currentBookingStep, isCapturingBasicInfo) => {
  if (BookingProcess.isStepRequiredInfoCapture(currentBookingStep)) {
    return true;
  }
  if (currentBookingStep === BookingSteps.Dashboard) {
    return false;
  }
  return isCapturingBasicInfo;
};

const isSlotTimeAfterUserTimezone = (
  slotTime,
  gymTimezone,
  now: Date,
) => moment.tz(slotTime, gymTimezone).isSameOrAfter(moment(now).startOf('hour'));

const handleUnavailableSlot = (slot: ITimeSlotWithEligibility, gymTimezone: string) => {
  slot.isUnavailable = isTooLateToBookSlot(slot.startTime, gymTimezone);
  return slot;
};

const isTooLateToBookSlot = (startTime: number, gymTimezone: string) => {
  const timeInCorrectTimezone = moment.tz(startTime, gymTimezone);
  const lastTimeToAllowBookingAfterStarted = moment().subtract(minutesToAllowBookingsAfterClassStarted, 'minutes');
  return timeInCorrectTimezone.isSameOrBefore(lastTimeToAllowBookingAfterStarted);
};

export const bookingWizard = produce((
  draft: IBookingWizardState = defaultWizardState,
  action: any,
): IBookingWizardState => {
  switch (action.type) {
    case types.SET_IS_CUSTOMISING_WORKOUT:
      const { isCustomisingWorkout } = action.payload;
      draft.isCustomisingWorkout = isCustomisingWorkout;
      break;
    case types.OPEN_PERSONALISE_WORKOUT_SETUP_SCREEN:
      const { screen } = action.payload;
      screen === PersonaliseSetupScreens.WorkoutType || screen === PersonaliseSetupScreens.BodyGroup
        ? draft.personaliseSetupWarning = PersonaliseSetupWarning.CustomiseWarning
        : draft.personaliseSetupWarning = null;

      const { workoutSettings } = action.payload;
      const { gymType } = action.payload;
      const isBalanceWorkout = workoutSettings?.cardioOrResistance === CardioOrResistance.Balance;
      if (workoutSettings && gymType) {
        const canCustomiseBodyGroup = WorkoutSettings.isBodyGroupsCustomizationDisabled(workoutSettings, gymType);
        if (screen === PersonaliseSetupScreens.BodyGroup && canCustomiseBodyGroup) {
          draft.personaliseSetupWarning = PersonaliseSetupWarning.BodyGroupEquipmentWarning;
        }
      }
      const { allowToSetEquipment } = action.payload;
      if (screen === PersonaliseSetupScreens.Equipment && !allowToSetEquipment) {
        draft.personaliseSetupWarning = PersonaliseSetupWarning.EquipmentWarning;
      }

      if (screen === PersonaliseSetupScreens.BagContact && isBalanceWorkout) {
        draft.personaliseSetupWarning = PersonaliseSetupWarning.BagContactBalanceWarning;
      }

      if (screen === PersonaliseSetupScreens.BodyGroup && isBalanceWorkout) {
        draft.personaliseSetupWarning = PersonaliseSetupWarning.BodyGroupBalanceWarning;
      }
      draft.currentPersonaliseSetupScreen = screen;
      break;
    case types.CLEAR_PERSONALISE_SETUP_WARNING:
      draft.personaliseSetupWarning = null;
      break;
    case types.GET_NEXT_BOOKING_STEP:
      const isSoloWorkout = action.payload.isSoloWorkout !== undefined
        ? action.payload.isSoloWorkout
        : draft.isSoloWorkout;

      draft.fromPlanner = action.payload.fromPlanner;
      draft.isFirstWorkout = draft.isFirstWorkout || action.payload.isFirstWorkout;
      setNextBookingStep(draft, action, action.payload.processType, isSoloWorkout);
      break;
    case types.GET_PREVIOUS_BOOKING_STEP:
      draft.studioZones = [];
      if (draft.currentBookingStep) {
        const previousBookingInfo = BookingProcess.getPreviousStep(
          draft.previousBookingSteps,
          draft.currentBookingStep,
        );
        if (draft.currentBookingStep === previousBookingInfo.bookingStep) {
          resetWizard(draft, null);
          draft.isBookingModalVisible = false;
          break;
        }
        draft.currentBookingStep = previousBookingInfo.bookingStep;
        draft.progressBar = previousBookingInfo.progressBar;
        draft.makeBookingError = null;
      }
      break;
    case types.SET_SOLO_WORKOUT:
      draft.isSoloWorkout = action.payload.isSoloWorkout;
      const { userProfile } = action.payload;
      const isUserAmbassador = !!(userProfile && BaseUserProfile.isInfluencer(userProfile));
      const isAmbassadorGroupWorkout = !draft.isSoloWorkout && isUserAmbassador;
      if (isAmbassadorGroupWorkout) {
        initializeLiveStreamOptionsIfUndefined(draft);
        draft.livestreamToggleValue = isAmbassadorGroupWorkout;
      } else {
        delete draft.livestreamOptions;
      }
      break;
    case types.SET_WORKOUT_SESSION:
      const { workoutSession } = action.payload;
      if (workoutSession) {
        draft.currentSelectedGymName = workoutSession.gymName;
      }
      draft.workoutSession = workoutSession;
      break;
    case types.SET_SELECTED_GYM:
      draft.currentSelectedGymName = action.payload.currentSelectedGymName;
      const gym = GymResolver.getByName(action.payload.currentSelectedGymName);
      if (gym.type === GymTypes.Indoor && gym.singleStationPerZoneGym) {
        draft.isSoloWorkout = true;
      }
      draft.isOfficeScheduleBooking = gym.type === GymTypes.Home && gym.officeGymConfig && action.payload.atStudio;
      break;
    case types.SET_INVITES:
      draft.invites = action.payload.invites;
      break;
    case types.SET_WAITLIST_ENTRY:
      draft.waitlistEntry = action.payload.waitlistEntry;
      break;
    case types.FETCH_PARTNER_PROFILES_REQUEST:
      draft.isFetchingPartnerProfiles = true;
      break;
    case types.FETCH_PARTNER_PROFILES_SUCCESS:
      draft.isFetchingPartnerProfiles = false;
      draft.partnerProfiles = action.payload.partnerProfiles;
      draft.invites = action.payload.invites;
      break;
    case types.FETCH_PARTNER_PROFILES_FAILED:
      draft.isFetchingPartnerProfiles = false;
      draft.partnerProfiles = action.payload.partnerProfiles;
      break;
    case types.SET_LONG_TERM_GOAL:
      draft.longTermGoal = action.payload.longTermGoal;
      break;
    case types.GET_BOOKING_SLOTS_REQUEST:
      draft.isLoadingBookingSlots = true;
      draft.homeSlots = [];
      draft.bookingSlotsError = null;
      draft.studioSlots = defaultStudioSlots;
      break;
    case types.CLEAR_LOADED_SLOTS:
      draft.homeSlots = [];
      draft.bookingSlotsError = null;
      draft.studioSlots = defaultStudioSlots; // old field?
      draft.studioZones = [];
      break;
    case types.GET_HOME_BOOKING_SLOTS_SUCCESS:
      draft.isLoadingBookingSlots = false;
      draft.homeSlots = action.payload.homeSlots;
      draft.bookingSlotsError = null;
      ensureCurrentBookingIsInOpenSlot(draft, action.payload.homeSlots);
      break;
    case types.GET_STUDIO_BOOKING_SLOTS_SUCCESS:
      const timesForKnownAreas = action.payload.selectClassTimeData
        .filter((areaTimes) => knownAreasWhenAppWasBuilt.includes(areaTimes.areaCategory));
      const studioZones = timesForKnownAreas;
      const studioSlots = studioZones.flatMap(
        (zone) => zone.availableTimeSlots.flatMap(
          (timeslot) => timeslot.classTimes,
        ),
      );
      studioSlots.forEach((slot) => slot.isSelected = false);
      const hasNoAvailableSlots = studioSlots.length <= 0;

      draft.isLoadingBookingSlots = false;
      draft.studioZones = studioZones;
      draft.pendingSlot = null;
      draft.bookingSlotsError = hasNoAvailableSlots ? i18n.t('bookings:noSlotsAvailable') : null;

      break;
    case types.GET_BOOKING_SLOTS_FAILURE:
      draft.isLoadingBookingSlots = false;
      draft.homeSlots = [];
      draft.bookingSlotsError = action.payload.error;
      break;
    case types.SET_PENDING_SLOT:
      draft.studioZones.forEach((zone) => {
        zone.availableTimeSlots.forEach((slot) => {
          slot.classTimes.forEach((time) => {
            if (time.scheduleId === action.payload.pendingSlot?.scheduleId
              && zone.areaCategory === action.payload.areaCategory) {
              time.isSelected = true;
            } else if (time.isSelected) {
              time.isSelected = false;
            }
          });
        });
      });
      draft.bookingSlotsError = null;
      draft.pendingSlot = action.payload.pendingSlot;
      draft.areaCategory = action.payload.areaCategory;
      if (draft.allUserRecommendedSettings && draft.areaCategory) {
        draft.recommendedSettings = selectRecommendedSettingsBasedOnArea(
          draft.allUserRecommendedSettings,
          draft.areaCategory,
        );
      } else {
        draft.recommendedSettings = null;
      }
      break;
    case types.SET_DURATION:
      draft.workoutType = action.payload.workoutType;
      if (draft.pendingSlot) {
        draft.pendingSlot.workoutType = action.payload.workoutType;
      }
      break;
    case types.MAKE_BOOKING_REQUEST:
    case types.CHANGE_BOOKING_REQUEST:
      draft.makeBookingLoading = true;
      draft.makeBookingError = null;
      break;
    case types.MAKE_BOOKING_SUCCESS:
      draft.makeBookingLoading = false;
      draft.workoutSession = action.payload.bookingJustCreated;
      draft.makeBookingError = null;
      break;
    case types.CHANGE_BOOKING_SUCCESS:
      draft.makeBookingLoading = false;
      draft.workoutSession = action.payload.workoutSession;
      draft.makeBookingError = null;
      break;
    case types.MAKE_BOOKING_FAILURE:
    case types.CHANGE_BOOKING_FAILURE:
      draft.makeBookingLoading = false;
      draft.makeBookingError = action.payload.error;
      break;
    case types.GET_RECOMMENDED_SETTINGS_REQUEST:
      draft.recommendedSettingsLoading = true;
      draft.allUserRecommendedSettings = null;
      break;
    case types.UPDATE_SETTINGS:
      draft.recommendedSettingsLoading = false;
      draft.recommendedSettings = action.payload.settings;
      break;
    case types.GET_RECOMMENDED_SETTINGS_SUCCESS:
      draft.recommendedSettingsLoading = false;
      draft.allUserRecommendedSettings = action.payload.recommended;

      if (draft.allUserRecommendedSettings && draft.currentSelectedGymName) {
        const selectedGym = GymResolver.getByName(draft.currentSelectedGymName);
        if (selectedGym.type === GymTypes.Home) {
          draft.recommendedSettings = selectRecommendedSettingsBasedOnArea(
            draft.allUserRecommendedSettings,
            'home',
          );
        }
      }

      break;
    case types.GET_RECOMMENDED_SETTINGS_FAILURE:
      draft.recommendedSettingsLoading = false;
      draft.allUserRecommendedSettings = null;
      break;
    case types.SELECT_SKILL_LEVEL_REQUEST:
      draft.skillLevelLoading = true;
      draft.skillLevelError = null;
      break;
    case types.SELECT_SKILL_LEVEL_SUCCESS:
      draft.skillLevelLoading = false;
      draft.skillLevelError = null;
      break;
    case types.SELECT_SKILL_LEVEL_FAILURE:
      draft.skillLevelLoading = false;
      draft.skillLevelError = action.payload.error;
      break;
    case types.SELECT_STUDIO_PARTNER:
      draft.studioPartner = action.payload.partner;
      break;
    case types.RESET_WIZARD:
      resetWizard(draft, action);
      break;
    case types.START_WORKOUT_NOW_REQUEST:
      draft.makeBookingLoading = true;
      draft.isSoloWorkout = true;
      draft.currentSelectedGymName = action.payload.gymName;
      if (!draft.pendingSlot) {
        draft.pendingSlot = {};
      }
      draft.pendingSlot.isInstant = true;
      draft.pendingSlot.startTime = Date.now();
      draft.pendingSlot.workoutType = draft.workoutType;
      draft.day = buildDayForBooking(moment(), /* isToday */ true);
      draft.workoutSession = null;
      break;
    case types.START_WORKOUT_NOW_SUCCESS:
      draft.makeBookingLoading = false;
      break;
    case types.START_WORKOUT_NOW_FAILURE:
      draft.makeBookingLoading = false;
      draft.makeBookingError = action.payload.error;
      break;
    case types.SETUP_INSTANT_WORKOUT:
      resetWizard(draft, action);
      draft.isSoloWorkout = true;
      draft.currentSelectedGymName = action.payload.gymName;
      if (!draft.pendingSlot) {
        draft.pendingSlot = {};
      }
      draft.pendingSlot.isInstant = true;
      draft.pendingSlot.startTime = Date.now();
      draft.day = action.payload.day;
      draft.workoutSession = null;
      break;
    case types.DATE_TIME_SELECTED:
      const tempDate = moment(action.payload.date);
      const date = tempDate.valueOf();
      setDateAndBookingInformation(draft, tempDate, date);
      draft.pendingSlot = {
        startTime: date,
        workoutType: draft.workoutType,
      };
      draft.bookingSlotsError = null;
      break;
    case types.SET_DAY_TEMP:
      draft.day = action.payload.day;
      break;
    case types.BOOKING_MODAL_VISIBLE:
      draft.isBookingModalVisible = action.visible;
      break;
    case types.SETUP_HR_MONITOR_VIEW_SUPPORTED_DEVICES_PRESSED:
      draft.showSupportedDevices = true;
      break;
    case types.SETUP_HR_MONITOR_VIEW_SUPPORTED_DEVICES_CLOSED:
      draft.showSupportedDevices = false;
      break;
    case types.CREATE_DEMO_STUDIO_BOOKING_REQUEST:
      draft.creatingDemoBookingInProgress = true;
      break;
    case types.CREATE_DEMO_STUDIO_BOOKING_SUCCESS:
      draft.creatingDemoBookingInProgress = false;
      break;
    case types.CREATE_DEMO_STUDIO_BOOKING_FAILURE:
      draft.creatingDemoBookingInProgress = false;
      draft.bookingSlotsError = action.payload.errorMessage;
      break;
    case types.CHECKIN_REQUEST:
      draft.checkInLoading = true;
      draft.checkInError = null;
      break;
    case types.CHECKIN_SUCCESS:
      draft.checkInLoading = false;
      draft.workoutSession = action.payload.workoutSession;
      break;
    case types.CHECKIN_FAILURE:
      draft.checkInLoading = false;
      draft.checkInError = action.payload.errorMessage;
      break;
    case types.WAITINGLIST_REQUEST:
      draft.waitlistLoading = true;
      break;
    case types.WAITINGLIST_SUCCESS:
      draft.waitlistLoading = false;
      break;
    case types.WAITINGLIST_FAILURE:
      draft.waitlistLoading = false;
      draft.bookingSlotsError = action.payload.errorMessage;
      break;
    case WAITINGLIST_BOOKING_REQUEST:
      draft.bookWaitlistSlotLoading = true;
      draft.bookWaitlistSlotError = null;
      draft.currentSelectedGymName = draft.waitlistEntry?.gymName || null;
      break;
    case WAITINGLIST_BOOKING_SUCCESS:
      const { waitlistEntry } = draft;
      const timeSlot = waitlistEntry?.timeSlot;
      draft.bookWaitlistSlotLoading = false;
      draft.currentSelectedGymName = waitlistEntry?.gymName || null;
      draft.pendingSlot = timeSlot;
      draft.day = buildDayForBooking(moment(timeSlot?.startTime), /* isToday */ false);
      setNextBookingStep(draft, action, ProcessTypes.WorkoutSettings, true);
      break;
    case WAITINGLIST_BOOKING_FAILURE:
      draft.bookWaitlistSlotLoading = false;
      draft.bookWaitlistSlotError = action.payload.error;
      break;
    case REMOVE_WAITLIST_ENTRY:
      setNextBookingStep(draft, action, ProcessTypes.ConfirmCancelWaitlist, true);
      draft.bookWaitlistSlotError = null;
      break;
    case CHANGE_WAITLIST_ENTRY:
      const waitlist = draft.waitlistEntry;
      draft.currentSelectedGymName = waitlist?.gymName || null;
      draft.day = buildDayForBooking(moment(waitlist?.timeSlot.startTime), /* isToday */ false);
      setNextBookingStep(draft, action, ProcessTypes.ChangeBooking, true);
      break;
    case SCHEDULE_OR_START_WORKOUT_BUTTON_PRESSED:
      draft.showStartOrScheduleOnDurationStep = true;
      break;
    case BOOK_WORKOUT_SLIDER_CARD_PRESSED:
      draft.showStartOrScheduleOnDurationStep = true;
      break;
    case types.LIVE_STREAM_OPTION_TOGGLED:
      draft.livestreamToggleValue = !draft.livestreamToggleValue;
      break;
    case types.PRE_WARMUP_TALK_TIME_OPTION_TOGGLED:
      getLiveStreamOptions(draft).preWorkoutTalk = !getLiveStreamOptions(draft).preWorkoutTalk;
      break;
    case types.WILL_DROP_OFF_AFTER_TALK_TOGGLED:
      const oldSetting = getLiveStreamOptions(draft).streamerWillDropOffAfterTalk;
      getLiveStreamOptions(draft).streamerWillDropOffAfterTalk = !oldSetting;
      break;
    case types.LIVE_STREAM_OPTIONS_NEXT_PRESSED:
      if (!draft.livestreamToggleValue || !draft.livestreamOptions?.preWorkoutTalk) {
        delete draft.livestreamOptions;
        break;
      }
      getLiveStreamOptions(draft).preWorkoutTalkTopic = action.payload.preWorkoutTalkTopic;
      break;
    case types.WORKOUT_TYPE_SELECTED_IN_BOOKING_STEP:
      draft.wokoutTypeWarning = action.payload.cardioOrResistance;
      break;
    case types.START_ON_DEMAND_STUDIO_BOOKING_REQUEST:
      draft.startOnDemandBookingLoading = true;
      draft.onDemandStudioBooking = true;
      break;
    case types.START_ON_DEMAND_STUDIO_BOOKING_SUCCESS:
      draft.workoutSession = action.payload.booking;
      draft.startOnDemandBookingLoading = false;
      break;
    case types.START_ON_DEMAND_STUDIO_BOOKING_FAILED:
      draft.makeBookingError = action.payload.error;
      draft.onDemandStudioBooking = false;
      draft.startOnDemandBookingLoading = false;
      break;
    case types.ON_DEMAND_STUDIO_SELECT_SLOT_BACK_PRESSED:
      draft.onDemandStudioBooking = false;
      break;
    case types.REFRESH_EXPIRING_ON_DEMAND_SLOTS_REQUEST:
      draft.onDemandStudioBooking = true;
      draft.loadingOnDemandSlots = true;
      draft.pendingSlot = null;
      break;
    case types.REFRESH_EXPIRING_ON_DEMAND_SLOTS_SUCCESS:
      draft.loadingOnDemandSlots = false;
      draft.onDemandSlots = action.payload.onDemandSlots;
      draft.pendingSlot = null;
      break;
    case types.REFRESH_EXPIRING_ON_DEMAND_SLOTS_FAILURE:
      draft.loadingOnDemandSlots = false;
      delete draft.onDemandSlots;
      break;
    case types.CHECK_IN_ON_DEMAND_BOOKING_PRESS_REQUEST:
      draft.checkingInOnDemandWorkout = true;
      break;
    case types.CHECK_IN_ON_DEMAND_BOOKING_PRESS_FAILURE:
      draft.checkingInOnDemandWorkout = false;
      break;
    case types.CHECK_IN_ON_DEMAND_BOOKING_PRESS_SUCCESS:
      draft.checkingInOnDemandWorkout = false;
      break;
    case types.UPDATING_BOXING_SETTINGS_DURING_CHECKIN_REQUEST:
      draft.updatingBoxingSetting = true;
      break;
    case types.UPDATING_BOXING_SETTINGS_DURING_CHECKIN_SUCCESS:
      draft.updatingBoxingSetting = false;
      break;
    case types.UPDATING_BOXING_SETTINGS_DURING_CHECKIN_FAILURE:
      draft.updatingBoxingSetting = false;
      draft.checkInError = action.payload.errorMessage;
      break;
    case types.UPGRADE_STUDIO_PLAN_PRESSED:
      draft.subscriptionPortalLoading = true;
      draft.bookingSlotsError = null;
      break;
    case types.UPGRADE_STUDIO_PLAN_PRESSED_SUCCESS:
      draft.subscriptionPortalLoading = false;
      draft.bookingSlotsError = null;
      break;
    case types.UPGRADE_STUDIO_PLAN_PRESSED_FAILED:
      draft.subscriptionPortalLoading = false;
      draft.bookingSlotsError = 'Failed to open the subscription portal. Please contact support.';
      break;
  }
  return draft;
});

const defaultStudioSlots = {
  thirtyMinuteWorkoutSlots: [],
  fiftyMinuteWorkoutSlots: [],
};

const resetWizard = (draft: IBookingWizardState, action: any) => {
  draft.workoutSession = null;
  draft.currentBookingStep = undefined;
  draft.invites = [];
  draft.partnerProfiles = {};
  draft.waitlistEntry = null;
  draft.isFetchingPartnerProfiles = false;
  draft.longTermGoal = ProgramType.NotSet;
  draft.isCapturingBasicInfo = false;
  draft.isSoloWorkout = null;
  draft.previousBookingSteps = [];
  draft.isLoadingBookingSlots = false;
  draft.homeSlots = [];
  draft.studioSlots = defaultStudioSlots;
  draft.bookingSlotsError = null;
  draft.pendingSlot = null;
  draft.recommendedSettingsLoading = false;
  draft.recommendedSettings = null;
  draft.workoutType = null;
  draft.skillLevelLoading = false;
  draft.skillLevelError = null;
  draft.studioPartner = null;
  if (action && action.payload.userProfile) {
    const gym = GymResolver.getByName(action.payload.userProfile.gymName);
    draft.currentSelectedGymName = gym.type === GymTypes.Home ? gym.name : null;
  }
  draft.isCustomisingWorkout = false;
  draft.currentPersonaliseSetupScreen = PersonaliseSetupScreens.Overview;
  draft.day = null;
  draft.makeBookingError = null;
  draft.showStartOrScheduleOnDurationStep = false;
  draft.livestreamOptions = undefined;
  draft.livestreamToggleValue = false;
  draft.loadingOnDemandSlots = false;
  draft.onDemandStudioBooking = false;
  draft.checkingInOnDemandWorkout = false;
  draft.cancelOnDemandBookingLoading = false;
  draft.updatingBoxingSetting = false;
  delete draft.onDemandSlots;
};

function setDateAndBookingInformation(draft: IBookingWizardState, tempDate: any, date: any) {
  if (!draft.day) {
    return;
  }
  draft.day.startOfDay = tempDate.startOf('day').valueOf();
  draft.day.date = date;
  draft.day.dayIndex = tempDate.day();
  draft.day.displayName = tempDate.format('dd');
  draft.day.isToday = tempDate.isSame(new Date(), 'day');
  draft.day.checkAfterEndDate = false;
}

const defaultLiveStreamOptions: ILiveStreamOptions = {
  streamerWillDropOffAfterTalk: false,
  preWorkoutTalk: true,
};

function initializeLiveStreamOptionsIfUndefined(draft: IBookingWizardState) {
  if (!draft.livestreamOptions) {
    draft.livestreamOptions = defaultLiveStreamOptions;
  }
}

function getLiveStreamOptions(draft: IBookingWizardState): ILiveStreamOptions {
  if (!draft.livestreamOptions) {
    draft.livestreamOptions = defaultLiveStreamOptions;
  }
  return draft.livestreamOptions;
}

function ensureCurrentBookingIsInOpenSlot(draft: IBookingWizardState, homeSlots: any) {
  const bookingSlot = homeSlots.find(
    (slot: any) => slot.startTime === moment(draft.pendingSlot.startTime).millisecond(0).valueOf(),
  );

  if (draft.pendingSlot.startTime < Date.now()) {
    draft.bookingSlotsError = i18n.t('bookings:slotPassed');
    return;
  }

  if (!bookingSlot) {
    draft.bookingSlotsError = i18n.t('bookings:noSlotsAvailable');
    return;
  }

  if (!bookingSlot.booked) {
    return;
  }

  if (draft.workoutSession && draft.workoutSession.uniqueReference === bookingSlot.workoutId) {
    return;
  }
  draft.bookingSlotsError = i18n.t('bookings:bookingConflict');
}

function setNextBookingStep(draft, action, processType, isSoloWorkout) {
  const { currentBookingStep } = draft;
  const { enabledFeatures } = action.payload;
  const currentProgressBar = draft.progressBar;
  if (processType) {
    draft.processType = processType;
  }
  const bookingProcessInfo = getCurrentBookingProcessInfo(
    draft,
    action,
    currentBookingStep,
    draft.processType,
    enabledFeatures,
  );
  const isCapturingBasicInfo = getIsBasicInfoStep(bookingProcessInfo.bookingStep, draft.isCapturingBasicInfo);

  draft.isSoloWorkout = isSoloWorkout;
  draft.currentBookingStep = bookingProcessInfo.bookingStep;
  draft.progressBar = bookingProcessInfo.progressBar;
  draft.isCapturingBasicInfo = isCapturingBasicInfo;
  if (currentBookingStep) {
    draft.previousBookingSteps.push({
      bookingStep: currentBookingStep,
      progressBar: currentProgressBar,
    });
  }
}
